// src/utils/jwtUtils.ts
// Utility functions for JWT token manipulation (testing purposes only)

import { createLogger } from "./logger";

const log = createLogger("jwtUtils");

/**
 * Decode a JWT token payload without verification
 * JWT structure: header.payload.signature (base64url encoded)
 */
function decodeJwtPayload(token: string): Record<string, any> {
  const parts = token.split(".");
  if (parts.length !== 3) {
    throw new Error("Invalid JWT token format");
  }

  // Decode the payload (second part)
  const payload = parts[1];
  // Handle base64url encoding (replace - with + and _ with /)
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = atob(base64);
  return JSON.parse(jsonPayload);
}

/**
 * Encode a payload to base64url format
 */
function encodeBase64Url(str: string): string {
  const base64 = btoa(str);
  // Convert to base64url format
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Create a modified JWT token with a different tenant ID (tid)
 *
 * WARNING: This creates an unsigned/invalidly signed token.
 * Only use in development/testing where signature verification is disabled.
 *
 * @param originalToken - The original JWT token from Office SSO
 * @param newTenantId - The new tenant ID to inject
 * @returns Modified JWT token string
 */
export function modifyJwtTenantId(originalToken: string, newTenantId: string): string {
  try {
    const parts = originalToken.split(".");
    if (parts.length !== 3) {
      log.warn("Invalid JWT format, returning original token");
      return originalToken;
    }

    // Decode payload
    const payload = decodeJwtPayload(originalToken);
    const originalTid = payload.tid;

    // Modify the tid claim
    payload.tid = newTenantId;

    // Re-encode the payload
    const newPayloadBase64 = encodeBase64Url(JSON.stringify(payload));

    // Reconstruct token with original header and signature
    // Note: Signature will be invalid, but backend doesn't verify it
    const modifiedToken = `${parts[0]}.${newPayloadBase64}.${parts[2]}`;

    log.info("JWT tid modified for testing", {
      originalTid,
      newTid: newTenantId,
      tokenModified: true,
    });

    return modifiedToken;
  } catch (error) {
    log.error("Failed to modify JWT token", {
      error: error instanceof Error ? error.message : String(error),
    });
    return originalToken;
  }
}

/**
 * Extract the current tenant ID from a JWT token
 */
export function extractTenantId(token: string): string | null {
  try {
    const payload = decodeJwtPayload(token);
    return payload.tid || null;
  } catch {
    return null;
  }
}
